import { Box, TextField, Typography } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import * as React from "react";
import { useEffect } from "react";
import { useParams } from "react-router-dom";

interface UserTreeViewProps {
	selected_user_groups: string[];
	setSelectedUserGroups: (groups: string[]) => void;
	recipients: string[];
	setRecipients: (recipients: string[]) => void;
	recipientOptions: [string, string, string, string, string[], boolean][];
	allowed_user_groups: string[];
	groupOrder?: string[];
}

const syntheticGroups: Record<string, string[]> = {
	"Regulated Substances (RSU)": [
		"RSU",
		"RSU Phase I ",
		"RSU Phase II ",
		"RSU District 1 Consultant",
		"RSU CDB/CI "
	],
	"Natural Resources (NRU)": [
		"NRU",
		"NRU "
	],
	"Cultural Resources (CRU)": [
		"CRU",
		"CRU Archaeology",
		"CRU Architecture ",
		"CRU Historic Architecture Consultant",
	],
	"Local Roads": [
		"Local Roads District "
	],
	"D1,2,3-NIU": [
		"NIU Supervisor",
		"NIU-Wetlands",
		"NIU-Biological Surveys",
	],
	"INHS": [
		"UIUC Supervisor",
		"UIUC-Wetlands",
		"UIUC-Biological Surveys",
	],
	"D7,8,9-SIUC": [
		"SIUC Supervisor",
		"SIUC-Wetlands",
		"SIUC-Biological Surveys",
	],
	"ISGS": [
			"ISGS Wetlands",
			"ISGS PESA",
	],
};

const groupToSynthetic: Record<string, string> = Object.entries(
	syntheticGroups,
).reduce((acc, [parent, groups]) => {
	groups.forEach((g) => {
		acc[g] = parent;
	});
	return acc;
}, {} as Record<string, string>);

const UserTreeView: React.FC<UserTreeViewProps> = ({
	selected_user_groups,
	setSelectedUserGroups,
	recipients,
	setRecipients,
	recipientOptions,
	allowed_user_groups,
	groupOrder = [],
}) => {

	const { esrId = "", activeId } = useParams();
	const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
	const [searchTerm, setSearchTerm] = React.useState("");
	const expandedAllowedGroups = React.useMemo(() => {
		return allowed_user_groups.flatMap((group) => {
			switch (group) {
				case "D1,2,3-NIU":
					return [
						"NIU Supervisor",
						"NIU-Wetlands",
						"NIU-Biological Surveys",
					];

				case "D4,5,6-UIUC":
					return [
						"UIUC Supervisor",
						"UIUC-Wetlands",
						"UIUC-Biological Surveys",
					];

				case "D7,8,9-SIUC":
					return [
						"SIUC Supervisor",
						"SIUC-Wetlands",
						"SIUC-Biological Surveys",
					];
				case "ISGS":
					return [
						"ISGS Wetlands",
						"ISGS PESA",
					];
				default:
					return [group];
			}
		});
	}, [allowed_user_groups]);
	const treeData = React.useMemo(() => {
		return expandedAllowedGroups.map((group) => {
			const usersInGroup = recipientOptions.filter(
				([email, first, last, org, userGroups, isSponsor]) => userGroups.includes(group),
			);
			return {
				group,
				users: usersInGroup.map(([email, first, last]) => ({
					email,
					name: `${first} ${last}`,
				})),
			};
		});
	}, [expandedAllowedGroups, recipientOptions]);


	// Filter a single group entry by the search term (unchanged behavior)
	const filterGroupEntry = React.useCallback(
		(
			{ group, users }: { group: string; users: { email: string; name: string }[] },
			lower: string,
		) => {
			const groupMatches = group.toLowerCase().includes(lower);
			const filteredUsers = users.filter(
				({ name, email }) =>
					name.toLowerCase().includes(lower) ||
					email.toLowerCase().includes(lower),
			);
			if (groupMatches) return { group, users };
			if (filteredUsers.length > 0) return { group, users: filteredUsers };
			return null;
		},
		[],
	);

	// Split groups into synthetic-parented nodes and standalone groups
	const groupedTreeData = React.useMemo(() => {
		const lower = searchTerm.trim().toLowerCase();

		const syntheticEntries = new Map<
			string,
			{ group: string; users: { email: string; name: string }[] }[]
		>();
		const standaloneRaw: { group: string; users: { email: string; name: string }[] }[] = [];

		for (const entry of treeData) {
			let parent: string | undefined = undefined;
			Object.keys(groupToSynthetic).map((group => {
				console.log(`Checking if ${entry.group} starts with ${group}`, entry.group.startsWith(group));
				if (entry.group.startsWith(group)) {
					console.log("parent set: ", groupToSynthetic[group]);
					parent = groupToSynthetic[group];
				}

			}));
			if (parent !== undefined) {
				console.log(`Adding group ${entry.group} to synthetic parent ${parent}`);
				if (!syntheticEntries.has(parent)) syntheticEntries.set(parent, []);
				syntheticEntries.get(parent)!.push(entry);
			} else {
				standaloneRaw.push(entry);
			}
		}

		if (!lower) {
			return {
				syntheticNodes: Array.from(syntheticEntries.entries()).map(
					([parent, groups]) => ({ parent, groups }),
				),
				standalone: standaloneRaw,
			};
		}

		const standalone = standaloneRaw
			.map((entry) => filterGroupEntry(entry, lower))
			.filter(Boolean) as { group: string; users: { email: string; name: string }[] }[];

		const syntheticNodes = Array.from(syntheticEntries.entries())
			.map(([parent, groups]) => {
				if (parent.toLowerCase().includes(lower)) {
					return { parent, groups };
				}
				const filteredGroups = groups
					.map((entry) => filterGroupEntry(entry, lower))
					.filter(Boolean) as { group: string; users: { email: string; name: string }[] }[];
				if (filteredGroups.length === 0) return null;
				return { parent, groups: filteredGroups };
			})
			.filter(Boolean) as { parent: string; groups: { group: string; users: { email: string; name: string }[] }[] }[];

		return { syntheticNodes, standalone };
	}, [searchTerm, treeData, filterGroupEntry]);

	// Position of each name in `groupOrder` - matched entries sort into that
	// order, unmatched entries fall to the end in their existing relative order.
	const orderIndex = React.useMemo(() => {
		const map = new Map<string, number>();
		groupOrder.forEach((name, i) => {
			if (!map.has(name)) map.set(name, i);
		});
		return map;
	}, [groupOrder]);

	const orderPatterns = React.useMemo(() => {
		const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		return groupOrder
			.map((name, index) => ({ name, index }))
			.filter(({ name }) => /District X|County X/.test(name))
			.map(({ name, index }) => {
				const pattern = escapeRegExp(name)
					.replace(/District X/g, "District [1-9]")
					.replace(/County X/g, "County \\S+");
				return { regex: new RegExp(`^${pattern}$`), index };
			});
	}, [groupOrder]);

	const getOrder = React.useCallback(
		(name: string) => {
			const exact = orderIndex.get(name);
			if (exact !== undefined) return exact;
			const templated = orderPatterns.find(({ regex }) => regex.test(name));
			return templated?.index ?? Number.MAX_SAFE_INTEGER;
		},
		[orderIndex, orderPatterns],
	);

	// Synthetic parents always render as a block at the top of the tree
	// (ordered among themselves), followed by standalone groups in order.
	const orderedTopLevel = React.useMemo(() => {
		const synthetic = groupedTreeData.syntheticNodes
			.map((node) => ({
				kind: "synthetic" as const,
				parent: node.parent,
				groups: [...node.groups].sort(
					(a, b) => getOrder(a.group) - getOrder(b.group),
				),
			}))
			.sort((a, b) => getOrder(a.parent) - getOrder(b.parent));
		const standalone = groupedTreeData.standalone
			.map((node) => ({
				kind: "standalone" as const,
				group: node.group,
				users: node.users,
			}))
			.sort((a, b) => getOrder(a.group) - getOrder(b.group));
		return [...synthetic, ...standalone];
	}, [groupedTreeData, getOrder]);

	// Handle selection. Only leaf items (users, and groups with no users)
	// render a checkbox, so itemIds already only ever contains leaves.
	const handleSelect = (
		event: React.SyntheticEvent<Element, Event> | null,
		itemIds: string[],
	) => {
		setSelectedIds(itemIds);

		const groups = itemIds
			.filter((id) => id.startsWith("group::"))
			.map((id) => id.replace("group::", ""));

		const users = itemIds
			.filter((id) => id.startsWith("user::"))
			.map((id) => id.split("::")[2]);

		setSelectedUserGroups(groups);
		setRecipients(users);
	};

	useEffect(()=>{
		setSelectedIds([])
	}, [esrId, activeId, recipientOptions, allowed_user_groups,]);

	// Parent items (anything with children) are expandable but not selectable -
	// only leaf items can be checked. The checkbox is hidden via CSS rather than
	// unmounted so it still reserves its layout space, keeping the expand arrows
	// and checkboxes of every row aligned into the same columns.
	const hiddenCheckboxSlotProps = {
		checkbox: { style: { visibility: "hidden" as const } },
	};

	const renderNodeLabel = (text: string, hasChildren: boolean) =>
		hasChildren ? (
			<Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
				{text}
			</Typography>
		) : (
			text
		);

	return (
		<Box sx={{ minWidth: 320 }}>
			<Typography
				fontWeight={500}
				fontSize={13}
				color="text.secondary"
				sx={{ mb: 1 }}
			>
				Recipients:
			</Typography>

			<TextField
				size="small"
				variant="outlined"
				fullWidth
				placeholder="Search group, name, or email"
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
				sx={{ mb: 1 }}
			/>

			<SimpleTreeView
				multiSelect
				checkboxSelection
				selectedItems={selectedIds}
				onSelectedItemsChange={handleSelect}
				sx={{
					maxHeight: 300,
					overflowY: "auto",
					border: "1px solid #ddd",
					borderRadius: 1,
					p: 1,
				}}
			>
				{orderedTopLevel.length > 0 ? (
					<>
						{orderedTopLevel.map((node) =>
							node.kind === "synthetic" ? (
								<TreeItem
									key={`synthetic::${node.parent}`}
									itemId={`synthetic::${node.parent}`}
									label={renderNodeLabel(node.parent, true)}
									slotProps={hiddenCheckboxSlotProps}
								>
									{node.groups.map(({ group, users }) => {
										const hasChildren = users.length > 0;
										return (
											<TreeItem
												key={`group::${group}`}
												itemId={`group::${group}`}
												label={renderNodeLabel(group, hasChildren)}
												slotProps={
													hasChildren ? hiddenCheckboxSlotProps : undefined
												}
											>
												{users.map(({ email, name }) => (
													<TreeItem
														key={`user::${group}::${email}`}
														itemId={`user::${group}::${email}`}
														label={`${name} <${email}>`}
													/>
												))}
											</TreeItem>
										);
									})}
								</TreeItem>
							) : (
								(() => {
									const hasChildren = node.users.length > 0;
									return (
										<TreeItem
											key={`group::${node.group}`}
											itemId={`group::${node.group}`}
											label={renderNodeLabel(node.group, hasChildren)}
											slotProps={
												hasChildren ? hiddenCheckboxSlotProps : undefined
											}
										>
											{node.users.map(({ email, name }) => (
												<TreeItem
													key={`user::${node.group}::${email}`}
													itemId={`user::${node.group}::${email}`}
													label={`${name} <${email}>`}
												/>
											))}
										</TreeItem>
									);
								})()
							),
						)}
					</>
				) : (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ p: 2, textAlign: "center" }}
					>
						No results found
					</Typography>
				)}
			</SimpleTreeView>
		</Box>
	);
};

export default UserTreeView;
